import _ from 'lodash';

import { createClient } from '@/shared/api';
import FACILITY_QUERY from '@/entities/facility/api/queries/facility';
import type { FacilityQuery, FacilityQueryVariables } from '@/shared/api/generated/graphql';

export type FacilityClinicianContact = {
  name: string;
  phone: string;
  email: string;
  description: string;
};

export type Facility = {
  id: string;
  name: string;
  friendlyId: string;
  contact: {
    address: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    phone1: string;
    phone2: string;
    nightPhone: string;
    fax: string;
  };
  facilityNote: string;
  clinicianContacts: FacilityClinicianContact[];
  assignedSalesRepresentative: {
    id: string;
    firstName: string;
    lastName: string;
    contact: { phone1: string };
    email: string;
  };
};

/**
 * Loads a facility record by id. Runs the facility query and throws the first GraphQL error only when no facility came back at all.
 *
 * @param id - Identifier of the facility.
 * @returns A promise resolving to the facility.
 */
const fetchFacility = async (id: string) => {
  const client = await createClient();
  const facilityResult = await client.query<FacilityQuery, FacilityQueryVariables>({
    query: FACILITY_QUERY,
    variables: { id },
  });
  const { data, errors } = facilityResult;
  const { facility } = data;
  if (_.isNil(facility) && errors?.length) {
    throw new Error(errors[0].message);
  }
  return facility;
};

export default fetchFacility;
